import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode';

interface MobileSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  theme: any;
  asset?: string;
  onSubmissionSuccess?: () => void;
}

interface PaymentData {
  amount: number;
  bolt11: string;
  payment_hash: string;
  checking_id: string;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
  
  /* Enhanced mobile support */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  
  /* Prevent zoom on iOS */
  touch-action: manipulation;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid ${props => props.theme.border};
  
  /* Enhanced mobile scrolling */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  
  /* Prevent content jumping */
  box-sizing: border-box;
  
  /* Better shadow for mobile */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  padding: 20px 20px 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${props => props.theme.text};
  cursor: pointer;
  padding: 0;
  line-height: 1;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const SubmissionOption = styled.div`
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.theme.background};

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.hoverBackground};
  }
`;

const OptionIcon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
`;

const OptionTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const OptionDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${props => props.theme.subText};
  line-height: 1.4;
`;

const LinkSubmissionForm = styled.div`
  margin-top: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 16px; /* Prevent zoom on iOS */
  background: ${props => props.theme.inputBackground || props.theme.surface};
  color: ${props => props.theme.text};
  box-sizing: border-box;
  
  /* Enhanced mobile support */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none; /* Remove iOS styling */
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.subText};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 16px; /* Prevent zoom on iOS */
  background: ${props => props.theme.inputBackground || props.theme.surface};
  color: ${props => props.theme.text};
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
  
  /* Enhanced mobile support */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.subText};
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  /* Enhanced mobile touch */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  min-height: 48px; /* Accessibility minimum */
  
  &:hover {
    opacity: 0.9;
  }
  
  &:active {
    transform: scale(0.98);
    background: ${props => props.theme.primary + 'dd'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button`
  width: 100%;
  padding: 12px;
  background: transparent;
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 12px;
  
  &:hover {
    background: ${props => props.theme.hoverBackground};
  }
`;

// Payment-related styled components
const PaymentContainer = styled.div`
  text-align: center;
  padding: 16px 0;
`;

const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  
  img {
    max-width: 200px;
    width: 100%;
    height: auto;
    border-radius: 8px;
    background: white;
    padding: 12px;
  }
`;

const InvoiceText = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 12px;
  font-family: monospace;
  font-size: 10px;
  word-break: break-all;
  color: ${props => props.theme.text};
  margin-bottom: 12px;
  max-height: 80px;
  overflow-y: auto;
`;

const CopyButton = styled.button`
  width: 100%;
  padding: 14px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 12px;
  transition: background 0.2s ease;
  
  &:active {
    background: #0056b3;
  }
`;

const PaymentAmountSelector = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const AmountButton = styled.button<{ $selected?: boolean }>`
  padding: 10px 16px;
  border: 1px solid ${props => props.$selected ? props.theme.primary : props.theme.border};
  background: ${props => props.$selected ? props.theme.primary + '20' : props.theme.surface};
  color: ${props => props.theme.text};
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
`;

const PaymentStatus = styled.div<{ $status: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$status === 'paid' ? '#27ae60' : props.theme.primary};
  margin-bottom: 12px;
`;

const SuccessMessage = styled.div`
  background: rgba(46, 204, 113, 0.2);
  border: 2px solid rgba(46, 204, 113, 0.5);
  border-radius: 12px;
  padding: 24px 20px;
  margin-bottom: 16px;
  color: ${props => props.theme.text};
  text-align: center;
  
  /* Make it very prominent */
  box-shadow: 0 4px 16px rgba(46, 204, 113, 0.3);
  animation: successSlideIn 0.4s ease-out;
  
  @keyframes successSlideIn {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  div:first-child {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: rgba(39, 174, 96, 1);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.15);
  border: 1px solid rgba(231, 76, 60, 0.4);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  color: ${props => props.theme.text};
  text-align: center;
  
  /* Make it more prominent */
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.2);
`;

const MessageButton = styled.button`
  background: linear-gradient(135deg, #2ECC71 0%, #27AE60 100%);
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 25px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  box-shadow: 0 3px 12px rgba(46, 204, 113, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 18px rgba(46, 204, 113, 0.5);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(46, 204, 113, 0.3);
  }
`;

type SubmissionMode = 'select' | 'link' | 'manual' | 'payment';

const MobileSubmissionModal: React.FC<MobileSubmissionModalProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  theme,
  asset = 'btc',
  onSubmissionSuccess
}) => {
  const [mode, setMode] = useState<SubmissionMode>('select');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkContactInfo, setLinkContactInfo] = useState({
    email: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [formData, setFormData] = useState({
    expert_name: '',
    twitter_handle: '',
    prediction: '',
    target_date: '',
    submitter_email: ''
  });

  // Payment state
  const [paymentAmount, setPaymentAmount] = useState<number>(21);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'expired'>('pending');
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [pendingSubmissionData, setPendingSubmissionData] = useState<any>(null);
  const paymentCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup payment check interval on unmount
  useEffect(() => {
    return () => {
      if (paymentCheckIntervalRef.current) {
        clearInterval(paymentCheckIntervalRef.current);
      }
    };
  }, []);

  const resetForm = () => {
    setMode('select');
    setLinkUrl('');
    setLinkContactInfo({ email: '', name: '' });
    setFormData({
      expert_name: '',
      twitter_handle: '',
      prediction: '',
      target_date: '',
      submitter_email: ''
    });
    setMessage(null);
    // Reset payment state
    setPaymentAmount(21);
    setPaymentData(null);
    setPaymentStatus('pending');
    setQrCodeDataUrl('');
    setPendingSubmissionData(null);
    if (paymentCheckIntervalRef.current) {
      clearInterval(paymentCheckIntervalRef.current);
      paymentCheckIntervalRef.current = null;
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSuccessClose = () => {
    onSubmissionSuccess?.(); // Call success callback when user dismisses success message
    handleClose();
  };

  // Create Lightning invoice for payment
  const createInvoice = async () => {
    if (!pendingSubmissionData) return;
    
    setIsCreatingInvoice(true);
    try {
      const response = await fetch('/api/payments/create-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: paymentAmount,
          submissionData: pendingSubmissionData
        })
      });

      const data = await response.json();
      
      if (data.success && data.invoice) {
        // Generate QR code
        const qrDataUrl = await QRCode.toDataURL(data.invoice.payment_request, {
          width: 200,
          margin: 2,
          color: { dark: '#000000', light: '#FFFFFF' }
        });
        
        setPaymentData({
          amount: paymentAmount,
          bolt11: data.invoice.payment_request,
          payment_hash: data.invoice.payment_hash,
          checking_id: data.invoice.checking_id
        });
        setQrCodeDataUrl(qrDataUrl);
        
        // Start monitoring payment
        monitorPayment(data.invoice.checking_id);
      } else {
        setMessage({ type: 'error', text: 'Failed to create invoice. Please try again.' });
        setMode('manual');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setMessage({ type: 'error', text: 'Failed to create invoice. Please try again.' });
      setMode('manual');
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  // Monitor payment status
  const monitorPayment = (checkingId: string) => {
    const checkPayment = async () => {
      try {
        const response = await fetch(`/api/payments/check-payment/${checkingId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.paid) {
            setPaymentStatus('paid');
            if (paymentCheckIntervalRef.current) {
              clearInterval(paymentCheckIntervalRef.current);
              paymentCheckIntervalRef.current = null;
            }
            // Show success after short delay
            setTimeout(() => {
              setMessage({ 
                type: 'success', 
                text: 'Payment received! Your prediction has been submitted for review.' 
              });
              setMode('select'); // This will show the success message
            }, 1000);
          }
        }
      } catch (error) {
        console.error('Error checking payment:', error);
      }
    };

    // Check immediately
    checkPayment();
    
    // Then check every 2 seconds
    paymentCheckIntervalRef.current = setInterval(checkPayment, 2000);
    
    // Stop checking after 10 minutes (invoice expires)
    setTimeout(() => {
      if (paymentCheckIntervalRef.current) {
        clearInterval(paymentCheckIntervalRef.current);
        paymentCheckIntervalRef.current = null;
        if (paymentStatus === 'pending') {
          setPaymentStatus('expired');
          setMessage({ type: 'error', text: 'Invoice expired. Please try again.' });
        }
      }
    }, 10 * 60 * 1000);
  };

  // Copy invoice to clipboard
  const copyInvoice = async () => {
    if (!paymentData?.bolt11) return;
    
    try {
      await navigator.clipboard.writeText(paymentData.bolt11);
      // Brief feedback
      const btn = document.getElementById('copy-invoice-btn');
      if (btn) {
        btn.textContent = '✅ Copied!';
        setTimeout(() => {
          btn.textContent = '📋 Copy Lightning Invoice';
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Proceed to payment after form validation
  const proceedToPayment = (submissionData: any) => {
    setPendingSubmissionData(submissionData);
    setMode('payment');
  };

  const submitLink = async () => {
    if (!linkUrl.trim()) {
      setMessage({ type: 'error', text: 'Please enter a valid URL' });
      return;
    }

    // Basic URL validation
    if (!linkUrl.includes('twitter.com') && !linkUrl.includes('x.com')) {
      setMessage({ type: 'error', text: 'Please enter a valid Twitter/X.com URL' });
      return;
    }

    // Prepare submission data and proceed to payment
    const submissionData = {
      expert_name: 'From Link',
      twitter_handle: '',
      prediction_type: 'target',
      source_url: linkUrl,
      submitter_email: linkContactInfo.email || null,
      notes: `Mobile link submission: ${linkUrl}`,
      asset_type: asset || 'btc'
    };
    
    proceedToPayment(submissionData);
  };

  // Old direct submit (keeping for reference, but now goes through payment)
  const submitLinkDirect = async () => {
    if (!linkUrl.trim()) {
      setMessage({ type: 'error', text: 'Please enter a valid URL' });
      return;
    }

    // Basic URL validation
    if (!linkUrl.includes('twitter.com') && !linkUrl.includes('x.com')) {
      setMessage({ type: 'error', text: 'Please enter a valid Twitter/X.com URL' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/submissions/quick-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          linkUrl: linkUrl,
          type: 'link',
          userEmail: linkContactInfo.email || null,
          userName: linkContactInfo.name || null,
          submitted_via: 'mobile'
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ 
          type: 'success', 
          text: result.message || 'Link submitted successfully!'
        });
        setLinkUrl('');
        setLinkContactInfo({ email: '', name: '' });
        // Don't call onSubmissionSuccess immediately - let user see message first
        // onSubmissionSuccess?.();
        // Don't auto-close - let user read the message and close manually
      } else {
        setMessage({ type: 'error', text: 'Failed to submit. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    }

    setLoading(false);
  };

  const submitManual = async () => {
    if (!formData.expert_name || !formData.prediction || !formData.target_date) {
      setMessage({ type: 'error', text: 'Please fill in the expert name, prediction, and target date' });
      return;
    }

    // Prepare submission data and proceed to payment
    const submissionData = {
      expert_name: formData.expert_name,
      twitter_handle: formData.twitter_handle,
      prediction_type: 'target',
      price_prediction: formData.prediction,
      target_time: formData.target_date,
      submitter_email: formData.submitter_email || null,
      notes: `Mobile manual submission`,
      asset_type: asset || 'btc'
    };
    
    proceedToPayment(submissionData);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()} theme={theme}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>Submit {asset?.toUpperCase() || 'BTC'} Prediction</ModalTitle>
          <CloseButton onClick={handleClose} theme={theme}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {message && message.type === 'success' ? (
            <SuccessMessage theme={theme}>
              <div>✅ {message.text}</div>
              <MessageButton theme={theme} onClick={handleSuccessClose}>
                Got it!
              </MessageButton>
            </SuccessMessage>
          ) : (
            <>
              {message && message.type === 'error' && (
                <ErrorMessage theme={theme}>
                  <div>❌ {message.text}</div>
                </ErrorMessage>
              )}

              {mode === 'select' && (
            <>
              <SubmissionOption 
                onClick={() => setMode('link')} 
                theme={theme}
              >
                <OptionIcon>🔗</OptionIcon>
                <OptionTitle theme={theme}>Submit Link</OptionTitle>
                <OptionDescription theme={theme}>
                  Paste a Twitter/X.com link with a Bitcoin prediction
                </OptionDescription>
              </SubmissionOption>

              <SubmissionOption 
                onClick={() => setMode('manual')} 
                theme={theme}
              >
                <OptionIcon>✍️</OptionIcon>
                <OptionTitle theme={theme}>Manual Entry</OptionTitle>
                <OptionDescription theme={theme}>
                  Enter prediction details manually
                </OptionDescription>
              </SubmissionOption>
            </>
          )}

          {mode === 'link' && (
            <LinkSubmissionForm>
              <BackButton onClick={() => setMode('select')} theme={theme}>
                ← Back to options
              </BackButton>
              
              <FormGroup>
                <Label theme={theme}>Twitter/X.com URL *</Label>
                <Input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://twitter.com/username/status/..."
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Your Email (optional)</Label>
                <Input
                  type="email"
                  value={linkContactInfo.email}
                  onChange={(e) => setLinkContactInfo({...linkContactInfo, email: e.target.value})}
                  placeholder="your@email.com (we'll notify you when processed)"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Your Name (optional)</Label>
                <Input
                  type="text"
                  value={linkContactInfo.name}
                  onChange={(e) => setLinkContactInfo({...linkContactInfo, name: e.target.value})}
                  placeholder="Your name"
                  theme={theme}
                />
              </FormGroup>

              <Button
                onClick={submitLink}
                disabled={loading}
                theme={theme}
              >
                {loading ? 'Submitting...' : 'Submit Link'}
              </Button>
            </LinkSubmissionForm>
          )}

          {mode === 'manual' && (
            <LinkSubmissionForm>
              <BackButton onClick={() => setMode('select')} theme={theme}>
                ← Back to options
              </BackButton>
              
              <FormGroup>
                <Label theme={theme}>Expert Name *</Label>
                <Input
                  type="text"
                  value={formData.expert_name}
                  onChange={(e) => handleInputChange('expert_name', e.target.value)}
                  placeholder="e.g. Michael Saylor"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Twitter Handle</Label>
                <Input
                  type="text"
                  value={formData.twitter_handle}
                  onChange={(e) => handleInputChange('twitter_handle', e.target.value)}
                  placeholder="e.g. @saylor"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Prediction *</Label>
                <TextArea
                  value={formData.prediction}
                  onChange={(e) => handleInputChange('prediction', e.target.value)}
                  placeholder="e.g. Bitcoin will reach $150,000 by end of 2024"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Target Date</Label>
                <Input
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => handleInputChange('target_date', e.target.value)}
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Your Email (optional)</Label>
                <Input
                  type="email"
                  value={formData.submitter_email}
                  onChange={(e) => handleInputChange('submitter_email', e.target.value)}
                  placeholder="For updates on your submission"
                  theme={theme}
                />
              </FormGroup>

              <Button
                onClick={submitManual}
                disabled={loading}
                theme={theme}
              >
                {loading ? 'Submitting...' : 'Submit Prediction'}
              </Button>
            </LinkSubmissionForm>
          )}

          {mode === 'payment' && (
            <PaymentContainer>
              <BackButton onClick={() => {
                setMode('select');
                setPaymentData(null);
                setQrCodeDataUrl('');
                setPaymentStatus('pending');
                if (paymentCheckIntervalRef.current) {
                  clearInterval(paymentCheckIntervalRef.current);
                }
              }} theme={theme}>
                ← Back
              </BackButton>

              {!paymentData ? (
                // Amount selection and invoice creation
                <>
                  <h3 style={{ color: theme.text, marginBottom: '12px', fontSize: '16px' }}>
                    ⚡ Lightning Payment
                  </h3>
                  <p style={{ color: theme.subText, fontSize: '13px', marginBottom: '16px', lineHeight: '1.5' }}>
                    Time is our most valuable resource. We'll invest ours to check, verify and maintain your submission. You decide what that service is worth to you.
                  </p>

                  <FormGroup>
                    <Label theme={theme}>Amount (satoshis):</Label>
                    <Input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(Number(e.target.value) || 1)}
                      min={1}
                      max={100000}
                      placeholder="21"
                      theme={theme}
                    />
                    <div style={{ 
                      fontSize: '12px', 
                      color: theme.subText, 
                      marginTop: '6px',
                      textAlign: 'center'
                    }}>
                      ≈ ${(paymentAmount * 0.001).toFixed(2)} USD
                    </div>
                  </FormGroup>

                  <PaymentAmountSelector>
                    {[1, 21, 500, 1000, 2100].map(amount => (
                      <AmountButton
                        key={amount}
                        $selected={paymentAmount === amount}
                        onClick={() => setPaymentAmount(amount)}
                        theme={theme}
                      >
                        {amount} sats
                      </AmountButton>
                    ))}
                  </PaymentAmountSelector>

                  <Button
                    onClick={createInvoice}
                    disabled={isCreatingInvoice}
                    theme={theme}
                  >
                    {isCreatingInvoice ? 'Creating Invoice...' : 'Create Invoice'}
                  </Button>
                </>
              ) : (
                // Show QR code and payment status
                <>
                  {paymentStatus === 'pending' && (
                    <>
                      <PaymentStatus $status="pending" theme={theme}>
                        🔄 Waiting for payment...
                      </PaymentStatus>

                      <QRCodeContainer theme={theme}>
                        {qrCodeDataUrl && (
                          <img src={qrCodeDataUrl} alt="Lightning Payment QR Code" />
                        )}
                      </QRCodeContainer>

                      <InvoiceText theme={theme}>
                        {paymentData.bolt11}
                      </InvoiceText>

                      <CopyButton id="copy-invoice-btn" onClick={copyInvoice}>
                        📋 Copy Lightning Invoice
                      </CopyButton>

                      <p style={{ color: theme.subText, fontSize: '12px' }}>
                        Amount: {paymentData.amount} sats
                      </p>
                    </>
                  )}

                  {paymentStatus === 'paid' && (
                    <PaymentStatus $status="paid" theme={theme}>
                      ✅ Payment Received!
                    </PaymentStatus>
                  )}

                  {paymentStatus === 'expired' && (
                    <>
                      <PaymentStatus $status="expired" theme={theme}>
                        ⏰ Invoice Expired
                      </PaymentStatus>
                      <Button onClick={() => {
                        setPaymentData(null);
                        setQrCodeDataUrl('');
                        setPaymentStatus('pending');
                      }} theme={theme}>
                        Try Again
                      </Button>
                    </>
                  )}
                </>
              )}
            </PaymentContainer>
          )}
          
          </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MobileSubmissionModal;